import { Check, Circle, CircleDot } from 'lucide-react';

import { cn, sectionNumber } from '@/lib/utils';
import { STEPS, type StepDefinition } from './questions';

interface StepperNavProps {
  currentIndex: number; // 1-based to match STEPS[].index
  completedIds: Set<string>;
  onJump: (stepIndex: number) => void;
}

/**
 * Vertical step navigation. Shows all 14 steps with their section name,
 * a small status icon (current / done / pending), and click-to-jump
 * behaviour. Pure presentational — state lives in the parent.
 */
export function StepperNav({ currentIndex, completedIds, onJump }: StepperNavProps) {
  return (
    <nav aria-label="Questionnaire steps" className="space-y-1.5">
      <p className="smallcaps mb-3 text-graphite">Steps</p>
      <ol className="space-y-0.5">
        {STEPS.map((step) => (
          <li key={step.id}>
            <StepItem
              step={step}
              isCurrent={step.index === currentIndex}
              isComplete={completedIds.has(step.id)}
              isReachable={step.index <= currentIndex || completedIds.has(step.id)}
              onClick={() => onJump(step.index)}
            />
          </li>
        ))}
      </ol>
    </nav>
  );
}

function StepItem({
  step,
  isCurrent,
  isComplete,
  isReachable,
  onClick,
}: {
  step: StepDefinition;
  isCurrent: boolean;
  isComplete: boolean;
  isReachable: boolean;
  onClick: () => void;
}) {
  const Icon = isComplete ? Check : isCurrent ? CircleDot : Circle;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!isReachable}
      className={cn(
        'flex w-full items-center gap-3 rounded-sm py-1.5 px-2 text-left transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ochre',
        isCurrent
          ? 'bg-ink/5'
          : isReachable
            ? 'hover:bg-ink/[0.03]'
            : 'cursor-not-allowed opacity-40',
      )}
      aria-current={isCurrent ? 'step' : undefined}
    >
      <Icon
        className={cn(
          'h-3.5 w-3.5 shrink-0',
          isComplete ? 'text-moss' : isCurrent ? 'text-ochre' : 'text-graphite',
        )}
        aria-hidden
      />
      <span className="font-mono text-[10px] text-graphite tabular-nums">
        {sectionNumber(step.index)}
      </span>
      <span
        className={cn(
          'truncate text-xs',
          isCurrent ? 'text-ink font-medium' : 'text-graphite',
        )}
      >
        {step.section}
      </span>
    </button>
  );
}
